// services/research.services.ts
import { createAsyncThunk } from "@reduxjs/toolkit";
import { API_URL } from "../../../app/config";
import { axiosApiInstance } from "../../../app/axios";
import { ErrorToast, SuccessToast } from "../../../components/toast/Toasts";
import { IResearch } from "../researchModal";
import moment from "moment";

export const getAllResearches = createAsyncThunk(
  "Research/FindAllResearch",
  async () => {
    try {
      const response = await axiosApiInstance.post(
        `${API_URL}/Research/FindAllResearch`,
        {
          Title: "",
          PropertyTypeName: "",
        },
      );
      return response.data?.Data?.length > 0
        ? response.data.Data?.map((item: any) => ({
            ...item,
            Date: item.Date
              ? moment(item.Date, "YYYY-MM-DD").format("DD/MM/YYYY")
              : "-",
            IsDisplayHome: item.IsDisplayHome ? "Yes" : "No",
          }))
        : [];
    } catch (error: any) {
      console.error("Error Fetching Researches:", error);
      return Promise.reject({
        message: error?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);

export const createResearch = createAsyncThunk(
  "Research/InsertResearch",
  async (researchData: any) => {
    try {
      const response = await axiosApiInstance.post(
        `${API_URL}/Research/InsertResearch`,
        researchData,
      );
      SuccessToast(response.data?.Details || "Success");
      return response.data;
    } catch (error: any) {
      ErrorToast(error?.response?.data?.Details || "Something went wrong");
      console.error("Error creating Research:", error);
      return Promise.reject({
        message: error?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);

export const updateResearch = createAsyncThunk(
  "Research/UpdateResearch",
  async (researchData: any) => {
    try {
      const response = await axiosApiInstance.post(
        `${API_URL}/Research/UpdateResearch`,
        researchData,
      );
      SuccessToast(response.data?.Details || "Success");
      return response.data;
    } catch (error: any) {
      ErrorToast(error?.response?.data?.Details || "Something went wrong");
      console.error("Error updating Research:", error);
      return Promise.reject({
        message: error?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);

export const deleteResearch = createAsyncThunk(
  "Research/DeleteResearch",
  async (ID: number) => {
    try {
      const response = await axiosApiInstance.post(
        `${API_URL}/Research/DeleteResearch`,
        { ID },
      );
      SuccessToast(response.data?.Details || "Success");
      return response.data;
    } catch (error: any) {
      ErrorToast(error?.response?.data?.Details || "Something went wrong");
      console.error("Error deleting Research:", error);
      return Promise.reject({
        message: error?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);

export const getResearchByID = createAsyncThunk(
  "Research/FindByIDResearch",
  async (ID: number) => {
    try {
      const response = await axiosApiInstance.post(
        `${API_URL}/Research/FindByIDResearch`,
        { ID },
      );
      const result = response.data?.Data;
      return {
        ID: result?.ID,
        Title: result?.Title,
        URL: result?.URL,
        Tagline: result?.Tagline,
        Date: result?.Date ? moment(result.Date, "YYYY-MM-DD").toDate() : "",
        PropertyTypeID: result?.PropertyTypeID,
        ShortDescription: result?.ShortDescription,
        LongDescription: result?.LongDescription ?? "",
        SequenceNo: result?.SequenceNo,
        IsDisplayHome: result?.IsDisplayHome || false,
        MetaTitle: result?.MetaTitle ?? "",
        MetaKeywords: result?.MetaKeywords ?? "",
        MetaDescription: result?.MetaDescription ?? "",
        MainImage: result?.MainImage,
        BannerImage: result?.BannerImage,
        IsActive: result?.IsActive ? 1 : 2,
      } as IResearch;
    } catch (error: any) {
      ErrorToast(error?.response?.data?.Details || "Something went wrong");
      console.error("Error fetching Research:", error);
      return Promise.reject({
        message: error?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);

export const getResearchByURL = createAsyncThunk(
  "Research/FindByURLResearch",
  async (URL: string) => {
    try {
      const response = await axiosApiInstance.post(
        `${API_URL}/Research/FindByURLResearch`,
        { URL },
      );
      const result = response.data?.Data;
      return {
        ID: result?.ID,
        Title: result?.Title,
        URL: result?.URL,
        Tagline: result?.Tagline,
        Date: result?.Date ?? "",
        PropertyTypeID: result?.PropertyTypeID,
        ShortDescription: result?.ShortDescription,
        LongDescription: result?.LongDescription ?? "",
        SequenceNo: result?.SequenceNo,
        IsDisplayHome: result?.IsDisplayHome || false,
        MetaTitle: result?.MetaTitle ?? "",
        MetaKeywords: result?.MetaKeywords ?? "",
        MetaDescription: result?.MetaDescription ?? "",
        MainImage: result?.MainImage,
        BannerImage: result?.BannerImage,
        IsActive: result?.IsActive ? 1 : 2,
      } as IResearch;
    } catch (error: any) {
      ErrorToast(error?.response?.data?.Details || "Something went wrong");
      console.error("Error fetching Research by URL:", error);
      return Promise.reject({
        message: error?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);

export const getAllActiveResearches = createAsyncThunk(
  "Research/FindAllActiveResearch",
  async () => {
    try {
      const response = await axiosApiInstance.get(
        `${API_URL}/Research/FindAllActiveResearch`,
      );
      return response.data?.Data;
    } catch (error: any) {
      console.error("Error Fetching Active Researches:", error);
      return Promise.reject({
        message: error?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);

export const activeInactiveResearch = createAsyncThunk(
  "Research/ActiveInActiveResearch",
  async (formData: { ID: number; IsActive: boolean }) => {
    try {
      const response = await axiosApiInstance.post(
        `${API_URL}/Research/ActiveInActiveResearch`,
        formData,
      );
      SuccessToast(response.data?.Details || "Status updated successfully");
      return response.data;
    } catch (error: any) {
      ErrorToast(error?.response?.data?.Details || "Something went wrong");
      console.error("Error active-inactive Research:", error);
      return Promise.reject({
        message: error?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);

export const researchImageUpload = createAsyncThunk(
  "Research/SingleImageUpload",
  async (formData: any) => {
    try {
      const response = await axiosApiInstance.post(
        `${API_URL}/Research/SingleImageUpload`,
        formData,
        { headers: { "content-type": "multipart/form-data" } },
      );
      SuccessToast(response.data?.Details || "Images uploaded successfully");
      return response.data;
    } catch (error: any) {
      ErrorToast(error?.response?.data?.Details || "Something went wrong");
      console.error("Error uploading Research images:", error);
      return Promise.reject({
        message: error?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);
